import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SessionState, MaterializedView } from '@/types';

interface SessionStore {
  // Current session state
  session: SessionState | null;
  materializedView: MaterializedView | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // EdgeHub integration
  edgeHubToken: string | null;
  edgeHubUserId: string | null;
  edgeHubUserName: string | null;
  scenarioFromUrl: string | null;
  isEdgeHubSession: boolean;

  // Actions
  setSession: (session: SessionState) => void;
  setMaterializedView: (mv: MaterializedView) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearSession: () => void;
  setEdgeHubContext: (token: string, userId: string, userName: string) => void;
  setScenarioFromUrl: (scenarioId: string | null) => void;
}

export const useSessionStore = create<SessionStore>()(
  persist(
    (set) => ({
      // Initial state
      session: null,
      materializedView: null,
      isLoading: false,
      error: null,

      // EdgeHub integration
      edgeHubToken: null,
      edgeHubUserId: null,
      edgeHubUserName: null,
      scenarioFromUrl: null,
      isEdgeHubSession: false,

      // Actions
      setSession: (session) => set({ session }),
      setMaterializedView: (materializedView) => set({ materializedView }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      clearSession: () => set({
        session: null,
        materializedView: null,
        error: null
      }),
      setEdgeHubContext: (token, userId, userName) => set({
        edgeHubToken: token,
        edgeHubUserId: userId,
        edgeHubUserName: userName,
        isEdgeHubSession: true,
      }),
      setScenarioFromUrl: (scenarioId) => set({ scenarioFromUrl: scenarioId }),
    }),
    {
      name: 'edgeplay-session',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
      // Only persist the EdgeHub auth fields — session/MV are re-fetched from the API.
      partialize: (state) => ({
        edgeHubToken: state.edgeHubToken,
        edgeHubUserId: state.edgeHubUserId,
        edgeHubUserName: state.edgeHubUserName,
        isEdgeHubSession: state.isEdgeHubSession,
      } as unknown as SessionStore),
    }
  )
);

interface UIStore {
  // Active interface
  activeInterface: 'email' | 'slack' | 'calendar' | 'documents';
  
  // Modal states
  emailComposerOpen: boolean;
  selectedEmailId: string | null;
  selectedChannelId: string | null;
  selectedChannelType: 'channel' | 'dm';

  // Notification system
  notifications: Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    timestamp: Date;
  }>;
  
  // Actions
  setActiveInterface: (activeInterface: 'email' | 'slack' | 'calendar' | 'documents') => void;
  setEmailComposerOpen: (open: boolean) => void;
  setSelectedEmailId: (id: string | null) => void;
  setSelectedChannel: (id: string | null, type?: 'channel' | 'dm') => void;
  addNotification: (notification: Omit<UIStore['notifications'][0], 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
}

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  activeInterface: 'email',
  emailComposerOpen: false,
  selectedEmailId: null,
  selectedChannelId: null,
  selectedChannelType: 'channel',
  notifications: [],
  
  // Actions
  setActiveInterface: (activeInterface) => set({ activeInterface }),
  setEmailComposerOpen: (emailComposerOpen) => set({ emailComposerOpen }),
  setSelectedEmailId: (selectedEmailId) => set({ selectedEmailId }),
  setSelectedChannel: (selectedChannelId, type = 'channel') => set({ selectedChannelId, selectedChannelType: type }),
  addNotification: (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
    };
    set({ notifications: [...get().notifications, newNotification] });
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      get().removeNotification(newNotification.id);
    }, 5000);
  },
  removeNotification: (id) => set({ 
    notifications: get().notifications.filter(n => n.id !== id) 
  }),
}));